import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import NRHRoomVal from "../../app/routes/apptest/NrhRoomVal/NRHRoomVal";
import { MmiTab } from "../../app/routes/bimai/mccos/MMITab/MmiTab";
import PNS from "../../app/routes/pns/PNS";
import UserItemsInProject from "../../app/routes/projectdashboard/ActiveAssignedItems/UserItemsInProject";
import EmbeddedLinkWidget from "../../app/routes/projectdashboard/EmbeddedLinkWidget/EmbeddedLinkWidget";
import ImageSlideWidget from "../../app/routes/projectdashboard/ImageSlideWidget/ImageSlideWidget";
import LinksWidget from "../../app/routes/projectdashboard/LinksWidget/LinksWidget";
import MeetingSeries from "../../app/routes/projectdashboard/MeetingSeries/MeetingSeries";
import News from "../../app/routes/projectdashboard/News/News";
import TimelineWidget from "../../app/routes/projectdashboard/TimelineWidget/TimelineWidget";
import { useAppSelector } from "../../hooks";
import { EmbedTypeEnum } from "@mcapps/shared/sharedConstants";
import { NRH_Spaces_ByggJ } from "../Custom_MCBIM/NRH/SpacesByggJ/NRH_Spaces_ByggJ";
import OrgChartWidget from "../OrgChartWidget/OrgChartWidget";
import OrgListWidget from "../OrgListWidget/OrgListWidget";
import WidgetWrapper from "../WidgetWrapper/WidgetWrapper";
import "./CustomGrid.css";
import { Flex } from "@mantine/core";

export interface IWidgetTemplate {
  w: number;
  h: number;
  i: string;
  label: string;
  limitToProject?: string;
  component: JSX.Element;
  useWrapper?: boolean;
  wrapperProps?: {
    allowFullscreen?: boolean;
  };
}

export const allWidgetsArray: IWidgetTemplate[] = [
  {
    w: 4,
    h: 8,
    i: "orgChart",
    label: "Organisjonskart",
    component: <OrgChartWidget />,
  },
  {
    w: 4,
    h: 8,
    i: "orgList",
    label: "Medlemmer",
    component: <OrgListWidget />,
  },
  {
    w: 6,
    h: 8,
    i: "news",
    label: "Oppslagstavle",
    component: <News />,
  },
  {
    w: 12,
    h: 8,
    i: "EmbeddedLinks",
    label: "Eksterne apper",
    component: <EmbeddedLinkWidget embedType={EmbedTypeEnum.EMBEDDEDLINK} />,
  },
  {
    w: 8,
    h: 8,
    i: "VideoFeed",
    label: "Byggeplasskamera",
    component: <EmbeddedLinkWidget embedType={EmbedTypeEnum.VIDEOFEED} />,
  },
  {
    w: 12,
    h: 8,
    i: "ImageSlide",
    label: "Bilder",
    component: <ImageSlideWidget />,
  },
  {
    w: 9,
    h: 3,
    i: "timeline",
    label: "Milepæler",
    component: <TimelineWidget />,
  },
  {
    w: 4,
    h: 10,
    i: "tasks",
    label: "Dine oppgaver",
    component: <UserItemsInProject app={"dashboard"} />,
  },
  {
    w: 7,
    h: 12,
    i: "meetingSeries",
    label: "Kalender",
    component: <MeetingSeries />,
  },
  {
    w: 7,
    h: 6,
    i: "links",
    label: "Lenker",
    component: <LinksWidget />,
  },
  {
    w: 5,
    h: 15,
    i: "nrh_mmi", //didnt work to change the value of i, got blank widget
    label: "MMI",
    component: <MmiTab />,
    useWrapper: true,
    wrapperProps: { allowFullscreen: true },
  },
  {
    w: 5,
    h: 15,
    i: "nrh_roomval", //didnt work to change the value of i, got blank widget
    limitToProject: "5f6085966d375136b9835f7a",
    label: "NRH Room Validation",
    component: <NRHRoomVal />,
    useWrapper: true,
    wrapperProps: { allowFullscreen: true },
  },
  {
    w: 5,
    h: 15,
    i: "spaces_byggj",
    label: "NRH Spaces Bygg J",
    component: <NRH_Spaces_ByggJ />,
    limitToProject: "5f6085966d375136b9835f7a",
    useWrapper: true,
    wrapperProps: { allowFullscreen: true },
  },
  {
    w: 10,
    h: 8,
    i: "pns",
    label: "PNS",
    component: <PNS viewerMode={true} />,
    useWrapper: true,
    wrapperProps: { allowFullscreen: true },
  },
];
const MIN_DASHBOARD_GRID_WIDTH = 1200;
const dashboardGridCols = 20;
const dashboardGridMargin: [number, number] = [10, 10];
const dashboardResizeHandles = ["s", "se", "e"];
const getDashboardLayoutItem = (widget) => ({
  i: widget.i,
  x: widget.x ?? 0,
  y: widget.y ?? Infinity,
  w: widget.w,
  h: widget.h,
  minW: widget.minW ?? 1,
  maxW: widget.maxW ?? 20,
  minH: widget.minH ?? 2,
  maxH: widget.maxH ?? 40,
  resizeHandles: widget.resizeHandles ?? dashboardResizeHandles,
});

const ReactGridLayout = ({ layoutGrid, setLayout }) => {
  const editMode = useAppSelector((state) => state.projectDashboardReducer.editMode);
  const ref = useRef(true);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(MIN_DASHBOARD_GRID_WIDTH);
  const dashboardLayoutGrid = useMemo(() => layoutGrid?.map(getDashboardLayoutItem) ?? [], [layoutGrid]);

  useLayoutEffect(() => {
    const updateGridWidth = () => {
      const measuredWidth = Math.floor(gridContainerRef.current?.clientWidth ?? 0);
      if (measuredWidth > 0) {
        setGridWidth(Math.max(measuredWidth, MIN_DASHBOARD_GRID_WIDTH));
      }
    };

    updateGridWidth();

    const resizeObserver = new ResizeObserver(updateGridWidth);
    if (gridContainerRef.current != null) {
      resizeObserver.observe(gridContainerRef.current);
    }
    window.addEventListener("resize", updateGridWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateGridWidth);
    };
  }, []);

  useEffect(() => {
    const firstRender = ref.current;
    if (firstRender === true) {
      ref.current = false;
    }
  });

  const handleLayoutChange = (layout) => {
    if (editMode !== true) {
      return null;
    }
    const firstRender = ref.current;
    if (firstRender === true) {
      return null;
    }
    if (layout.length > 0 && !firstRender) {
      setLayout(layout);
    }
  };
  return (
    <Flex className="d-flex" ref={gridContainerRef}>
      <GridLayout
        className="dashboardGridLayout"
        width={gridWidth}
        onLayoutChange={handleLayoutChange}
        verticalCompact={true}
        layout={dashboardLayoutGrid}
        isDraggable={editMode}
        draggableHandle={".reactGridItemHeader"}
        isResizable={editMode}
        preventCollision={false}
        useCSSTransforms={false}
        cols={dashboardGridCols}
        rowHeight={30}
        margin={dashboardGridMargin}
      >
        {dashboardLayoutGrid.map((widget) => {
          const widgetRender: IWidgetTemplate = allWidgetsArray.find((item) => item.i === widget.i);

          return (
            <div
              className="reactGridItem"
              key={widget.i}
              data-grid={{
                i: widget.i,
                x: widget.x ?? 0,
                y: widget.y ?? Infinity,
                w: widget.w,
                h: widget.h,
                minW: 1,
                maxW: 20,
                minH: 2,
                maxH: 40,
                resizeHandles: dashboardResizeHandles,
              }}
            >
              <div className="w-100 h-100 position-relative">
                <div className={`${editMode === true ? "reactGridItemHeader" : ""}`}></div>
                {widgetRender === undefined && <WidgetWrapper title={""} widgetKey={widget.i}></WidgetWrapper>}
                {widgetRender?.useWrapper === true ? (
                  <WidgetWrapper
                    title={widgetRender.label}
                    widgetKey={widget.i}
                    allowFullscreen={widgetRender?.wrapperProps?.allowFullscreen}
                  >
                    {widgetRender?.component}
                  </WidgetWrapper>
                ) : (
                  <>{widgetRender?.component}</>
                )}
              </div>
            </div>
          );
        })}
      </GridLayout>
    </Flex>
  );
};

export default ReactGridLayout;
